'use client';

import Image from 'next/image';
import Link from 'next/link';
import React, { AriaRole, HTMLAttributeAnchorTarget } from 'react';

import PlatformBadge, { PlatformBadgeType } from '../app-store-badge/PlatformBadge';
import BreadcrumbNavigation from '../breadcrumb/BreadcrumbNavigation';
import Button from '../button/Button';
import TwoColumnsOnLg from '../layout-components/TwoColumnsOnLg';

export type PageHeaderProps = {
    tag?: string;
    title?: string;
    subtitle?: string | React.JSX.Element;
    subtitle2?: string;
    // `false` is accepted so `customElement={isReady && <span/>}` works; both slots already
    // guard with `React.isValidElement` before rendering.
    customElement?: false | React.JSX.Element;
    customElement2?: false | React.JSX.Element;
    socialProof?: boolean;
    appStoreBadges?: boolean;
    buttonTitle?: string;
    buttonHref?: string;
    buttonHrefTarget?: HTMLAttributeAnchorTarget;
    buttonAction?: () => void;
    showBreadcrumbNavigation?: boolean;
} & (
    | { image?: StaticImageData | string; imageAlt: string; imageRole?: AriaRole }
    | {
          image?: undefined;
          imageAlt?: undefined;
          imageRole?: undefined;
      }
);

function WebsiteHeader(props: PageHeaderProps): React.JSX.Element {
    if (props.image && !props.imageAlt) throw new Error('If you use image prop then add an imageAlt prop also!');

    return (
        <TwoColumnsOnLg
            leftExtraClass="order-2 lg:order-1"
            left={
                <div className="flex flex-col w-full m-0 text-gray-light my-auto space-y-50 py-48 xl:py-0">
                    {props.showBreadcrumbNavigation && <BreadcrumbNavigation />}
                    <hgroup>
                        {props.tag && (
                            <div className="mb-24 uppercase text-cyan font-montserrat-semibold">{props.tag}</div>
                        )}
                        {props.title && <h1 className="header-title whitespace-pre-wrap">{props.title}</h1>}
                        {props.subtitle && <h2 className="mt-24 whitespace-pre-line text-body-20">{props.subtitle}</h2>}
                        {props.subtitle2 && (
                            <h2 className="mt-24 whitespace-pre-line text-body-20">{props.subtitle2}</h2>
                        )}
                    </hgroup>

                    {props.customElement && React.isValidElement(props.customElement) ? props.customElement : null}

                    {props.appStoreBadges && (
                        <div className="flex flex-row flex-wrap gap-24">
                            <PlatformBadge type={PlatformBadgeType.AppStore} />
                            <PlatformBadge type={PlatformBadgeType.PlayStore} />
                            <PlatformBadge type={PlatformBadgeType.WebVersion} />
                        </div>
                    )}

                    {props.buttonHref ? (
                        <Link
                            className=""
                            href={props.buttonHref}
                            target={props.buttonHrefTarget}>
                            <Button
                                className="w-full md:w-auto h-48"
                                onClick={() => {
                                    if (props.buttonAction) props.buttonAction();
                                }}>
                                {props.buttonTitle}
                            </Button>
                        </Link>
                    ) : (
                        props.buttonAction && (
                            <Button
                                className="w-full md:w-auto"
                                onClick={() => {
                                    if (props.buttonAction) props.buttonAction();
                                }}>
                                {props.buttonTitle}
                            </Button>
                        )
                    )}

                    {props.customElement2 && React.isValidElement(props.customElement2) ? props.customElement2 : null}
                </div>
            }
            rightExtraClass="order-1 lg:order-2 !self-auto"
            right={
                props.image ? (
                    <div className="relative flex flex-col min-h-full">
                        <Image
                            className="relative self-center object-contain w-full h-auto"
                            src={props.image}
                            width={800}
                            height={800}
                            alt={props.imageAlt}
                            role={props.imageRole}
                            priority
                        />
                    </div>
                ) : undefined
            }
        />
    );
}

export default WebsiteHeader;
